import { createContext, useContext, useMemo } from "react";

import {
  ApolloClient,
  OperationVariables,
  DocumentNode,
  TypedDocumentNode,
} from "@apollo/client";
import {
  useLazyQuery as _useLazyQuery,
  useMutation as _useMutation,
  useQuery as _useQuery,
} from "@apollo/client/react";

import { coalesce, doNothing } from "@kazoohr/helpers";
import {
  useTranslation,
  TFunction,
  TOptions,
} from "@kazoohr/internationalization";

import type { LazyQueryResult, MutationFunction, QueryResult } from "../common";

import { getHookDefaultErrorHandler } from "./getHookDefaultErrorHandler";
import { useQueryCompletedEffect, useQueryErrorEffect } from "./queryEffects";

/**
 * The execute function takes Apollo's own options (`variables` and `context`)
 * and returns a promise that always resolves. Apollo rejects it on errors and
 * on teardown (an `AbortError` on unmount), which would crash a caller that
 * fires and forgets. Errors are reported through the hook result and the
 * hook-level `onError` instead.
 */
export type LazyQueryExecFunction<
  TData,
  TVariables extends OperationVariables
> = (
  options?: _useLazyQuery.ExecOptions<TVariables>
) => ReturnType<_useLazyQuery.ResultTuple<TData, TVariables>[0]>;

export type LazyApiQueryTuple<TData, TVariables extends OperationVariables> = [
  LazyQueryExecFunction<TData, TVariables>,
  LazyQueryResult<TData, TVariables>
];

export type ApiMutationTuple<TData, TVariables extends OperationVariables> = [
  MutationFunction<TData, TVariables>,
  _useMutation.Result<TData>
];

export const ApiClientContext = createContext<{
  client: ApolloClient | undefined;
  defaultOnSuccess: (data: any) => void;
  defaultOnError: (error: any) => void;
}>({
  client: undefined,
  defaultOnSuccess: doNothing,
  defaultOnError: doNothing,
});

/**
 * Execute a query against the federated API.
 *
 * Unlike Apollo's `useQuery`, this accepts `onCompleted` and `onError`. They
 * run as effects on the committed result: `onCompleted` once loading ends with
 * data, `onError` once per distinct error. When no `onError` is given, the
 * error is toasted through `ApiClientContext`.
 */
export function useApiQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryOptions<TData, TVariables>
): QueryResult<TData, TVariables> {
  const resolvedOptions = coalesce(options, {});
  const onError = useErrorHandler(resolvedOptions);
  const { client } = useContext(ApiClientContext);
  const queryOptions = {
    errorPolicy: "none" as const,
    fetchPolicy: "cache-first" as const,
    notifyOnNetworkStatusChange: false,
    ...resolvedOptions,
    client,
  };
  const result = _useQuery(
    document,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    queryOptions as _useQuery.Options<TData, TVariables>
  );
  useQueryErrorEffect(result.error, onError);
  useQueryCompletedEffect(
    result.data,
    result.loading,
    resolvedOptions.onCompleted
  );
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as QueryResult<TData, TVariables>;
}

function useErrorHandler(options: {
  onError?: (error: any, ...rest: any[]) => void;
  defaultToastErrorMessageOrTranslationKey?: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  const { defaultOnError } = useContext(ApiClientContext);
  const { t } = useTranslation();
  return useMemo(() => {
    if (options.onError) {
      return options.onError;
    }
    return getHookDefaultErrorHandler(defaultOnError, t, options);
  }, [options.onError, options.defaultToastErrorMessageOrTranslationKey]);
}

export type QueryOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> =
  | _useQuery.Options<TData, TVariables> & {
      defaultToastErrorMessageOrTranslationKey?: string;
      onError?: (error: any) => void;
      onCompleted?: (data: TData) => void;
    };

/**
 * Execute a mutation against the federated API.
 */
export function useApiMutation<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationOptions<TData, TVariables>
): ApiMutationTuple<TData, TVariables> {
  const resolvedOptions = coalesce(options, {});
  const { client } = useContext(ApiClientContext);
  const onError = useErrorHandler(resolvedOptions);
  const onCompleted = useSuccessHandler(resolvedOptions);
  const mutationOptions = {
    errorPolicy: "none" as const,
    ...resolvedOptions,
    client,
    onError,
    onCompleted,
  };
  const result = _useMutation(
    document,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    mutationOptions as any
  );
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as ApiMutationTuple<TData, TVariables>;
}

function getDefaultSuccessHandler({
  toastFn,
  t,
  defaultToastSuccessMessageOrTranslationKey,
  defaultToastSuccessTranslationOptions,
}: {
  toastFn: (message: any) => void;
  t: TFunction;
  defaultToastSuccessMessageOrTranslationKey: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  return () => {
    toastFn(
      t(
        defaultToastSuccessMessageOrTranslationKey,
        defaultToastSuccessTranslationOptions
      )
    );
  };
}

function useSuccessHandler(options: {
  onCompleted?: (data: any, ...rest: any[]) => void;
  defaultToastSuccessMessageOrTranslationKey?: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  const { defaultOnSuccess } = useContext(ApiClientContext);
  const { t } = useTranslation();
  return useMemo(() => {
    const {
      defaultToastSuccessMessageOrTranslationKey,
      defaultToastSuccessTranslationOptions,
    } = options;

    if (!defaultToastSuccessMessageOrTranslationKey) {
      return options.onCompleted;
    }

    const successHandler = getDefaultSuccessHandler({
      toastFn: defaultOnSuccess,
      t,
      defaultToastSuccessMessageOrTranslationKey,
      defaultToastSuccessTranslationOptions,
    });

    return (data: any) => {
      options.onCompleted?.(data);
      successHandler();
    };
  }, [options.onCompleted, options.defaultToastSuccessMessageOrTranslationKey]);
}

export type MutationOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> =
  | _useMutation.Options<TData, TVariables> & {
      defaultToastSuccessMessageOrTranslationKey?: string;
      defaultToastSuccessTranslationOptions?: TOptions;
      defaultToastErrorMessageOrTranslationKey?: string;
      defaultToastErrorTranslationOptions?: TOptions;
    };

/**
 * Execute a query against the federated API on demand.
 *
 * Accepts the same `onCompleted` and `onError` options as `useApiQuery`. The
 * execute function is typed by `LazyQueryExecFunction`.
 */
export function useLazyApiQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyHookOptions<TData, TVariables>
): LazyApiQueryTuple<TData, TVariables> {
  const resolvedOptions = coalesce(options, {});
  const { client } = useContext(ApiClientContext);
  const onError = useErrorHandler(resolvedOptions);

  const lazyOptions = {
    errorPolicy: "none" as const,
    notifyOnNetworkStatusChange: false,
    ...resolvedOptions,
    client,
  };
  const [rawExecute, result] = _useLazyQuery(
    document,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    lazyOptions as _useLazyQuery.Options<TData, TVariables>
  );
  const execute = useMemo<LazyQueryExecFunction<TData, TVariables>>(
    () => (execOptions) =>
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      rawExecute(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        execOptions as _useLazyQuery.ExecOptions<TVariables>
      ).catch(() => ({ data: undefined, error: undefined })) as ReturnType<
        LazyQueryExecFunction<TData, TVariables>
      >,
    [rawExecute]
  );
  useQueryErrorEffect(result.error, onError);
  useQueryCompletedEffect(
    result.data,
    result.loading,
    resolvedOptions.onCompleted
  );
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return [execute, result] as LazyApiQueryTuple<TData, TVariables>;
}

export type LazyHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> =
  | _useLazyQuery.Options<TData, TVariables> & {
      defaultToastErrorMessageOrTranslationKey?: string;
      onError?: (error: any) => void;
      onCompleted?: (data: TData) => void;
    };
